import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check } from 'lucide-react';

const SearchableSelect = ({ options, value, onChange, placeholder, required, onAdd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  const selectedOption = options.find(opt => String(opt.id) === String(value));

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  const filteredOptions = options.filter(opt => 
    opt.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div 
        className={`w-full px-4 py-2 bg-gray-50 border ${isOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-gray-200'} rounded-lg cursor-pointer flex justify-between items-center transition-all`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`block truncate pr-4 ${selectedOption ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>
      
      {/* Hidden input for HTML validation if required */}
      {required && (
        <input 
          type="text" 
          required={required} 
          value={value || ''} 
          className="absolute opacity-0 w-0 h-0 p-0 m-0 border-0" 
          onChange={() => {}}
          tabIndex={-1}
        />
      )}

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50/50">
            <Search size={16} className="text-gray-400" />
            <input
              type="text"
              className="w-full focus:outline-none text-sm bg-transparent py-1"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(opt => (
                <div
                  key={opt.id}
                  className={`px-4 py-2 hover:bg-primary-50 cursor-pointer flex items-center justify-between text-sm ${String(value) === String(opt.id) ? 'bg-primary-50 text-primary-600 font-medium' : 'text-gray-700'}`}
                  onClick={() => {
                    onChange(opt.id);
                    setIsOpen(false);
                  }}
                >
                  <span className="block truncate pr-4" title={opt.name}>{opt.name}</span>
                  {String(value) === String(opt.id) && <Check size={16} className="flex-shrink-0" />}
                </div>
              ))
            ) : search.trim() && onAdd ? (
              <div
                className="px-4 py-3 hover:bg-primary-50 cursor-pointer text-sm text-primary-600 font-medium flex items-center gap-2"
                onClick={() => {
                  onAdd(search.trim());
                  setIsOpen(false);
                }}
              >
                <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-xs">+</span>
                </div>
                <span>Add "{search.trim()}"</span>
              </div>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No results found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
